import { foldersApi } from 'entities/folder/api/foldersApi';
import { unsorted, unsortedFrontName } from 'shared/lib/utils/utils';
import filter from 'lodash-es/filter';
import map from 'lodash-es/map';
import { makeAutoObservable, runInAction } from 'mobx';
import {
  ExplorerModelInterface,
  ExplorerEntityTypes,
  ContentItem,
  SortContentKey,
  SortContentOrder,
  ContentFolderItem,
} from 'entities/types';
import { FileModel } from 'entities/file/model/fileModel';
import { TableModel } from 'entities/table/model/tableModel';

export interface UpdatableFolder {
  id: string;
  name: string;
  section_id: string | null;
  parent_id: string | null;
}

export type UpdateFolderParams = {
  name: string;
  section_id?: string;
  parent_id?: string | null;
};

export type FolderData = {
  id: string;
  name: string;
  section_id: string;
  last_change_date: string;
  files_amount: number;
  is_search_match?: boolean;
  parent_id: string | null;
};

export type CreateFolderParams = {
  name: string;
  section_id?: string;
  parent_id?: string | null;
};

export type RestoreFolderParams = {
  sectionId: string;
  folderId?: string;
};
export type MergeFoldersParams = {
  first_folder_id: string;
  second_folder_id: string;
  new_folder_name: string;
  section_id: string;
};

export class FolderModel implements ExplorerModelInterface {
  public type = ExplorerEntityTypes.FOLDER as const;
  public isPending = false;
  public isError = false;

  private _sortKey: SortContentKey = SortContentKey.LAST_CHANGE_DATE;
  public get sortKey() {
    return this._sortKey;
  }
  public set sortKey(val: SortContentKey) {
    this._sortKey = val;
  }
  private _sortOrder: SortContentOrder = SortContentOrder.DESC;
  public get sortOrder() {
    return this._sortOrder;
  }
  public set sortOrder(val: SortContentOrder) {
    this._sortOrder = val;
  }

  public content: ContentItem[] | null = null;
  public files: FileModel[] | null = null;
  public tables: TableModel[] | null = null;
  public folders: FolderModel[] | null = null;
  public data: FolderData | null = null;
  private _id: string;
  public get id() {
    return this._id;
  }
  public get sectionId() {
    return this.data?.section_id;
  }
  public get name() {
    return this.data?.name === unsorted ? unsortedFrontName : this.data?.name;
  }

  // Данный костыль есть только в поиске, в остальных местах бэк откидывает эту папку
  public get isUnsortedFolder() {
    return this.name === unsortedFrontName;
  }

  constructor(idOrData: FolderData | string) {
    if (typeof idOrData === 'string') {
      this._id = idOrData;
    } else {
      this._id = idOrData.id;
      this.data = idOrData;
    }
    makeAutoObservable(this);
  }

  public setFiles(files: FileModel[]) {
    this.files = files;
  }
  public setTables(tables: TableModel[]) {
    this.tables = tables;
  }

  public localAddFolder(folderModel: FolderModel) {
    this.folders = [...(this.folders || []), folderModel];

    const newFolderContent: ContentFolderItem = {
      id: folderModel.id,
      name: folderModel.name || '',
      type: ExplorerEntityTypes.FOLDER,
      last_change_date: folderModel.data?.last_change_date || '',
      files_amount: folderModel.data?.files_amount || 0,
      section_id: folderModel.data?.section_id!,
      parent_id: folderModel.data?.parent_id!,
    };

    runInAction(() => {
      this.content = [newFolderContent, ...(this.content || [])];
    });
  }
  public async fetch({ in_trash }: { in_trash: string | null }) {
    runInAction(() => {
      this.isPending = true;
      this.isError = false;
    });

    try {
      const data = await foldersApi.fetch(this._id, { in_trash });

      runInAction(() => {
        this.data = data;
        this.isPending = false;
      });
    } catch (e) {
      runInAction(() => {
        this.isPending = false;
        this.isError = true;
      });
      console.error(e);
    }
  }

  public localDeleteFile(id: string) {
    if (this.files) {
      this.files = filter(this.files, (file) => file.id !== id);
    }

    this.content = filter(
      this.content || [],
      (item) => !(item.type === ExplorerEntityTypes.FILE && item.id === id)
    );
  }

  public localDeleteTable(id: string) {
    if (this.tables) {
      this.tables = filter(this.tables, (tables) => tables.id !== id);
    }

    this.content = filter(
      this.content || [],
      (item) => !(item.type === ExplorerEntityTypes.TABLE && item.id === id)
    );
  }

  public async getContent({ in_trash }: { in_trash: string | null }) {
    runInAction(() => {
      this.isPending = true;
      this.isError = false;
    });

    try {
      const res = await foldersApi.getContent(this._id, {
        in_trash,
        sort_by: this._sortKey,
        sort_direction: this._sortOrder,
      });
      this.content = res;

      runInAction(() => {
        this.isPending = false;
      });
    } catch (e) {
      runInAction(() => {
        this.isPending = false;
        this.isError = true;
      });
      console.error(e);
    }
  }

  public localDeleteFolders(ids: string[]) {
    if (this.folders) {
      this.folders = filter(this.folders, (folder) => !ids.includes(folder.id));
    }

    runInAction(() => {
      this.content = filter(
        this.content || [],
        (item) =>
          !(item.type === ExplorerEntityTypes.FOLDER && ids.includes(item.id))
      );
    });
  }

  public async fetchFolders({ in_trash }: { in_trash: string | null }) {
    runInAction(() => {
      this.isPending = true;
      this.isError = false;
    });

    try {
      const folders = await foldersApi.fetchFolders(this._id, { in_trash });

      runInAction(() => {
        this.folders = map(folders, (folder) => new FolderModel(folder));
        this.isPending = false;
      });
    } catch (e) {
      runInAction(() => {
        this.isPending = false;
        this.isError = true;
      });
      console.error(e);
    }
  }

  public async fetchFiles({ in_trash }: { in_trash: string | null }) {
    runInAction(() => {
      this.isPending = true;
      this.isError = false;
    });

    try {
      const files = await foldersApi.fetchFiles(this._id, { in_trash });

      runInAction(() => {
        this.files = map(files, (file) => new FileModel(file));
        this.isPending = false;
      });
    } catch (e) {
      runInAction(() => {
        this.isPending = false;
        this.isError = true;
      });
      console.error(e);
    }
  }

  public async fetchTables({ in_trash }: { in_trash: string | null }) {
    runInAction(() => {
      this.isPending = true;
      this.isError = false;
    });

    try {
      const tables = await foldersApi.fetchTables(this._id, { in_trash });

      runInAction(() => {
        this.tables = map(tables, (table) => new TableModel(table));
        this.isPending = false;
      });
    } catch (e) {
      runInAction(() => {
        this.isPending = false;
        this.isError = true;
      });
      console.error(e);
    }
  }
}
